// ── Service de Config (núcleo/aplicação) ──────────────────────────
// Regras/normalização em torno da tabela `config` (key-value JSONB).
// Recebe o repositório via porta (DI) — não conhece Supabase.

import type { Json } from '@/types/database.types';
import type { CommissionRates, WorkspaceOriginsConfig, ChannelOriginOption } from '@/types/domain';
import { DEFAULT_RATES } from '@utils';
import type { IConfigRepository } from '../ports/IConfigRepository';

// ─── helpers puros ──────────────────────────────────────────────

const normalizeItems = (arr: unknown): ChannelOriginOption[] =>
  (Array.isArray(arr) ? arr : [])
    .filter((c): c is { id?: string; name: string; funnel_id?: string | null } =>
      !!c && typeof c === 'object' && !!(c as { name?: string }).name)
    .map((c) => ({
      id: c.id ?? '',
      name: c.name,
      funnel_id: c.funnel_id || null,
    }));

const slugifyChannel = (name: string): string =>
  String(name || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);

export class ConfigService {
  constructor(private readonly repo: IConfigRepository) {}

  getConfigValue = <T = Json>(workspaceId: string, key: string): Promise<T | null> =>
    this.repo.getConfigValue<T>(workspaceId, key);

  setConfigValue = (workspaceId: string, key: string, value: Json): Promise<void> =>
    this.repo.setConfigValue(workspaceId, key, value);

  // ─── commission_rates ─────────────────────────────────────
  getCommissionRates = async (workspaceId: string): Promise<CommissionRates> => {
    const value = await this.getConfigValue<CommissionRates>(workspaceId, 'commission_rates');
    return value ?? (DEFAULT_RATES as CommissionRates);
  };

  setCommissionRates = async (workspaceId: string, rates: CommissionRates): Promise<void> => {
    await this.setConfigValue(workspaceId, 'commission_rates', rates as unknown as Json);
  };

  // ─── workspace_origens (normalização v4) ──────────────────
  getWorkspaceOrigins = async (workspaceId: string): Promise<WorkspaceOriginsConfig> => {
    try {
      const value = await this.getConfigValue<{
        version?: number;
        canais?: unknown;
        origens?: unknown;
      }>(workspaceId, 'workspace_origens');
      return {
        version: value?.version ?? 1,
        canais: normalizeItems(value?.canais),
        origens: normalizeItems(value?.origens),
      };
    } catch {
      return { version: 1, canais: [], origens: [] };
    }
  };

  setWorkspaceOrigins = async (
    workspaceId: string,
    data: Pick<WorkspaceOriginsConfig, 'canais' | 'origens'>,
  ): Promise<WorkspaceOriginsConfig> => {
    const cleanItems = (arr: unknown): ChannelOriginOption[] =>
      (Array.isArray(arr) ? arr : [])
        .filter((item): item is Partial<ChannelOriginOption> & { name: string } =>
          !!item &&
          typeof item === 'object' &&
          typeof (item as { name?: unknown }).name === 'string' &&
          !!(item as { name: string }).name.trim())
        .map((item) => {
          const name = item.name.trim();
          return {
            id: item.id || slugifyChannel(name),
            name,
            funnel_id: item.funnel_id || null,
          };
        });

    const payload: WorkspaceOriginsConfig = {
      version: 4,
      canais: cleanItems(data?.canais),
      origens: cleanItems(data?.origens),
    };

    await this.setConfigValue(workspaceId, 'workspace_origens', payload as unknown as Json);
    return payload;
  };

  getWorkspaceOriginCounts = async (workspaceId: string) => {
    const [channels, origins] = await Promise.all([
      this.repo.countLeadsByField(workspaceId, 'canal'),
      this.repo.countLeadsByField(workspaceId, 'origem'),
    ]);
    return { channels, origins };
  };
}
